/**
 * Multiplies every value in a flat { key: baseSize } map by scaleFactor, rounding
 * each to the nearest pixel. Used to scale a group of related TV Mode sizes
 * together without a separate Math.round(base * scaleFactor) per element.
 */
export const scaleSizes = (sizes, scaleFactor) => Object.fromEntries(
  Object.entries(sizes).map(([key, base]) => [key, Math.round(base * scaleFactor)]),
);

const groupIds = (arr) => [...(arr || [])].map((g) => g.id).sort().join(',');
const dateStr = (d) => `${d?.startDate ?? ''}|${d?.endDate ?? ''}`;
// Older templates were saved with category 'cardio' instead of 'R'.
export const normalizeCategory = (cat) => (cat === 'cardio' ? 'R' : cat);

/**
 * An empty group selection means "all groups the user has access to" — callers
 * resolve to the full accessible list before submitting, checking for
 * duplicates, or comparing against a saved template, so all three see the
 * same effective value.
 */
export const resolveSubmitGroups = (selectedGroups, allGroups) => (
  selectedGroups && selectedGroups.length > 0 ? selectedGroups : allGroups
);

/**
 * Returns the first template whose settings exactly match formValues, or null.
 * Pass excludeId to skip the template being edited/overwritten.
 */
export const findDuplicateTemplate = (formValues, templates, excludeId = null) => templates.find(
  (t) => {
    if (excludeId !== null && t.id === excludeId) return false;
    return (
      normalizeCategory(formValues.category) === normalizeCategory(t.category)
      && formValues.exerciseId === t.exerciseId
      && !!formValues.mustBeInAllGroups === !!t.mustBeInAllGroups
      && Number(formValues.gender) === Number(t.gender)
      && formValues.rangeType === t.rangeType
      && formValues.compareTo === t.compareTo
      && groupIds(formValues.groups) === groupIds(t.groups)
      && dateStr(formValues.rangeDates) === dateStr(t.rangeDates)
      && dateStr(formValues.compareDates) === dateStr(t.compareDates)
    );
  },
) || null;

/**
 * Filters templates to those covering at least one selected group, then sorts
 * by descending coverage ratio (matched selected groups / total selected groups).
 * Full coverage (ratio 1.0) sorts before partial matches; within the same ratio,
 * original order is preserved.
 */
export const filterAndSortByGroups = (templates, selectedGroups) => {
  if (!selectedGroups || selectedGroups.length === 0) return templates;

  const withRatio = templates.map((t) => {
    const matched = selectedGroups.filter(
      (sg) => t.groups.some((g) => g.id === sg.id),
    ).length;
    return { t, ratio: matched / selectedGroups.length };
  });

  return withRatio
    .filter(({ ratio }) => ratio > 0)
    .sort((a, b) => b.ratio - a.ratio || a.t.exerciseName.localeCompare(b.t.exerciseName))
    .map(({ t }) => t);
};
